import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Smartphone, ListChecks, Shirt, UserCheck, Info, Car, Phone,
  ClipboardCheck, Image as ImageIcon, Loader2, AlertTriangle,
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, testid }) => (
  <Card className="bg-white border border-gray-200 shadow-sm" data-testid={testid}>
    <CardContent className="p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
        <Icon className="w-4 h-4 text-cyan-600" /> {title}
      </h3>
      <div className="text-sm text-gray-700">{children}</div>
    </CardContent>
  </Card>
);

const asList = (v) => (Array.isArray(v) ? v : []);
const txt = (s) => (typeof s === 'string' ? s : (s?.label || s?.name || ''));

export default function AdminTalentPreview() {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/admin/shifts/${shiftId}/talent-preview`)
      .then((r) => setP(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load preview'))
      .finally(() => setLoading(false));
  }, [shiftId]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  if (error) return <div className="min-h-screen grid place-items-center text-slate-500">{error}</div>;

  const recapShots = asList(p.recap_required_shots);
  const grooming = asList(p.grooming_requirements);
  const dressChecklist = asList(p.dress_code_checklist);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-talent-preview">
      <div className="max-w-xl mx-auto px-4 py-5">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/shifts/${shiftId}`)} data-testid="preview-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to job
          </Button>
          <Badge className="bg-indigo-600 text-white flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Talent preview</Badge>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2 text-xs text-amber-800" data-testid="preview-banner">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          This is exactly what assigned talent see on their phone. The client bill rate is never shown here.
        </div>

        <div className="rounded-2xl overflow-hidden border border-slate-300 shadow-lg bg-white">
          <div className="bg-gradient-to-br from-slate-900 via-cyan-900 to-cyan-700 text-white p-4">
            <div className="text-xs opacity-80">{p.company_name}</div>
            <h2 className="text-xl font-bold">{p.event_title}</h2>
            <div className="text-white/80 text-sm mt-0.5">
              {p.role_name} · {p.is_date_tbd ? 'Date: TBD' : (p.shift_date || 'Date: TBD')} · {(!p.is_time_tbd && p.start_time) ? `${p.start_time}${p.end_time ? `–${p.end_time}` : ''}` : 'Time: TBD'}
              {p.hourly_rate ? ` · $${p.hourly_rate}/hr` : ''}
            </div>
            <div className="text-white/80 text-xs mt-1">{p.venue_name} · {p.city}, {p.state}</div>
          </div>

          <div className="p-4 space-y-3">
            {p.duties && (
              <Section icon={ListChecks} title="Job duties & responsibilities" testid="preview-duties">
                <p className="whitespace-pre-wrap leading-relaxed">{p.duties}</p>
              </Section>
            )}

            {(p.event_instructions || p.shift_description || p.arrival_instructions || p.entrance_instructions || p.onsite_directions || p.setup_instructions || p.parking_instructions || p.reporting_location) && (
              <Section icon={Info} title="Event instructions & logistics" testid="preview-instructions">
                <div className="space-y-1.5">
                  {(p.event_instructions || p.shift_description) && <p className="whitespace-pre-wrap">{p.event_instructions || p.shift_description}</p>}
                  {p.reporting_location && <div><b>Reporting location:</b> {p.reporting_location}</div>}
                  {p.arrival_instructions && <div><b>Arrival:</b> {p.arrival_instructions}</div>}
                  {p.entrance_instructions && <div><b>Entrance:</b> {p.entrance_instructions}</div>}
                  {p.onsite_directions && <div><b>Onsite:</b> {p.onsite_directions}</div>}
                  {p.setup_instructions && <div><b>Setup:</b> {p.setup_instructions}</div>}
                  {p.parking_instructions && <div className="flex gap-1"><Car className="w-4 h-4 text-slate-500 shrink-0" /><span><b>Parking:</b> {p.parking_instructions}</span></div>}
                </div>
              </Section>
            )}

            {(p.dress_code_name || p.dress_code_description || p.dress_code || dressChecklist.length > 0) && (
              <Section icon={Shirt} title="Dress code" testid="preview-dress">
                {p.dress_code_name && <div className="font-semibold">{p.dress_code_name}</div>}
                {(p.dress_code_description || p.dress_code) && <p className="whitespace-pre-wrap">{p.dress_code_description || p.dress_code}</p>}
                {dressChecklist.length > 0 && <ul className="list-disc list-inside mt-1">{dressChecklist.map((d, i) => <li key={i}>{txt(d)}</li>)}</ul>}
              </Section>
            )}

            {grooming.length > 0 && (
              <Section icon={UserCheck} title="Grooming requirements" testid="preview-grooming">
                <ul className="list-disc list-inside">{grooming.map((g, i) => <li key={i}>{txt(g)}</li>)}</ul>
              </Section>
            )}

            {p.contact_instructions && (
              <Section icon={Phone} title="Onsite contact" testid="preview-contact">
                <p className="whitespace-pre-wrap">{p.contact_instructions}</p>
              </Section>
            )}

            {(p.recap_required || p.recap_photo_min || recapShots.length > 0) && (
              <Section icon={ClipboardCheck} title="Reporting & documentation" testid="preview-reporting">
                <div className="space-y-1">
                  {p.recap_photo_min ? <div className="flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-cyan-600" /> At least {p.recap_photo_min} photo(s) required</div> : null}
                  {recapShots.length > 0 && <ul className="list-disc list-inside">{recapShots.map((s, i) => <li key={i}>{txt(s)}</li>)}</ul>}
                  {p.recap_required && <div>A post-event recap is required.</div>}
                </div>
              </Section>
            )}

            {p.organized_requirements && (() => {
              const o = p.organized_requirements;
              const labels = { event_briefing: 'Event briefing', performance_expectations: 'Performance expectations', reporting_requirements: 'Reporting', required_photos: 'Required photos', dress_code: 'Dress code', parking_logistics: 'Parking & logistics' };
              const text = Object.keys(labels).filter((k) => o[k] && String(o[k]).trim());
              const tasks = Array.isArray(o.task_checklist) ? o.task_checklist.filter(Boolean) : [];
              if (!text.length && !tasks.length) return null;
              return (
                <Section icon={ClipboardCheck} title="Job requirements (AI-organized)" testid="preview-organized">
                  <div className="space-y-1.5">
                    {text.map((k) => <div key={k}><b>{labels[k]}:</b> {o[k]}</div>)}
                    {tasks.length > 0 && <div><b>Checklist:</b><ul className="list-disc list-inside">{tasks.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
                  </div>
                </Section>
              );
            })()}

            {!p.duties && !p.event_instructions && !p.shift_description && !p.contact_instructions && (
              <div className="text-center text-sm text-slate-400 py-6" data-testid="preview-empty">
                No instructions entered yet — talent would see an empty booking. Add duties, instructions and reporting on the job, or use "Organize with AI".
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
